// Asset helpers — shared by the Assets list, editor, and detail page.
// Backend: Xano `/asset` (api:Bh0CIPqU), proxied at /api/assets*.

/* ── status ─────────────────────────────────────────────────────────────────
 * Free-text `status` on the asset row (like stock.type). Canonical set below;
 * an unknown value degrades to a neutral pill rather than disappearing. */
export const ASSET_STATUS = [
  { value: 'active',  label: 'ใช้งาน' },
  { value: 'repair',  label: 'กำลังซ่อม' },
  { value: 'broken',  label: 'ชำรุด/รอซ่อม' },
  { value: 'retired', label: 'ปลดระวาง' },
]

const STATUS_TONES = {
  active:  'bg-emerald-50 text-emerald-700 ring-emerald-200',
  repair:  'bg-amber-50 text-amber-700 ring-amber-200',
  broken:  'bg-rose-50 text-rose-700 ring-rose-200',
  retired: 'bg-slate-100 text-slate-500 ring-slate-200',
}

export function statusInfo(status) {
  const v = String(status || 'active').trim() || 'active'
  const found = ASSET_STATUS.find((s) => s.value === v)
  return {
    value: v,
    label: found ? found.label : v,
    tone: STATUS_TONES[v] || 'bg-slate-100 text-slate-600 ring-slate-200',
  }
}

/* ── age ─────────────────────────────────────────────────────────────────────
 * Years+months since the acquisition `date`. Returns { months, label } or null
 * when there is no acquisition date. Uses local calendar math (dates are
 * date-only "YYYY-MM-DD" → local midnight, correct in Asia/Bangkok). */
export function assetAge(date, now = new Date()) {
  const d = parseDate(date)
  if (!d) return null
  let months = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth())
  if (now.getDate() < d.getDate()) months -= 1
  if (months < 0) months = 0
  const y = Math.floor(months / 12)
  const m = months % 12
  let label
  if (y === 0 && m === 0) label = 'ใหม่'
  else if (y === 0) label = `${m} เดือน`
  else if (m === 0) label = `${y} ปี`
  else label = `${y} ปี ${m} เดือน`
  return { months, years: y, restMonths: m, label }
}

export function parseDate(v) {
  if (!v) return null
  if (typeof v === 'number') { const d = new Date(v); return isNaN(d) ? null : d }
  const s = String(v)
  // date-only → parse as local midnight; ISO/other → let Date handle it
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (m) { const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])); return isNaN(d) ? null : d }
  const d = new Date(s)
  return isNaN(d) ? null : d
}

// Thai date "1 ม.ค. 2568" (Buddhist year). Returns '—' when empty.
const TH_MONTHS_ABBR = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
export function fmtDateTH(v) {
  const d = parseDate(v)
  if (!d) return '—'
  return `${d.getDate()} ${TH_MONTHS_ABBR[d.getMonth()]} ${d.getFullYear() + 543}`
}

// ISO date-only "YYYY-MM-DD" for <input type=date>; '' when empty.
export function toDateInput(v) {
  const d = parseDate(v)
  if (!d) return ''
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function fmtMoney(n) {
  const v = Number(n)
  if (!Number.isFinite(v) || v === 0) return '—'
  return v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

/* ── asset_type catalog helpers ──────────────────────────────────────────────
 * The /api/asset-types feed is [{ id, Type, subtype, icon_url }]. Build an
 * id→row map, and a grouped {Type → [rows]} for the picker. */
export function indexTypes(rows) {
  const byId = new Map()
  const groups = new Map()
  for (const r of Array.isArray(rows) ? rows : []) {
    byId.set(Number(r.id), r)
    const g = r.Type || 'อื่นๆ'
    if (!groups.has(g)) groups.set(g, [])
    groups.get(g).push(r)
  }
  return { byId, groups }
}

export function typeLabel(byId, asset_type_id) {
  const r = byId?.get?.(Number(asset_type_id))
  if (!r) return '—'
  return r.subtype || r.Type || '—'
}

export function typeFull(byId, asset_type_id) {
  const r = byId?.get?.(Number(asset_type_id))
  if (!r) return '—'
  return r.Type ? `${r.Type} · ${r.subtype || ''}`.replace(/ · $/, '') : (r.subtype || '—')
}

// UI-only branch relabel (สนามเป้า is closed → shown as อารีย์). Never rewrite
// branch_id at the data layer.
export const relabelBranch = (name) => (/สนามเป้า/.test(name || '') ? 'อารีย์ (เดิมสนามเป้า)' : (name || '—'))

/* ── QR payload ──────────────────────────────────────────────────────────────
 * The QR encodes a deep link to the asset's detail page, so scanning it on a
 * phone opens the asset directly. Uses the current origin so it works in dev and
 * prod alike. `unique_id` rides along as a query param for future stickerless
 * verification, but the id is what routes. */
export function assetDeepLink(asset) {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://app.smileseasons.com'
  const uid = asset?.unique_id ? `?u=${encodeURIComponent(asset.unique_id)}` : ''
  return `${origin}/manager/assets/${asset?.id}${uid}`
}

// A short human tag printed under the QR on a label.
export function assetLabelLine(asset, byId) {
  const bits = [asset?.name, asset?.brand, asset?.model].filter(Boolean)
  return bits.join(' · ') || typeFull(byId, asset?.asset_type_id)
}

/* ── periodic checks ─────────────────────────────────────────────────────────
 * check[] entries carry created_at (epoch ms | timestamp). Return the latest as
 * a Date, or null when never checked. */
export function lastCheckAt(asset) {
  const list = Array.isArray(asset?.check) ? asset.check : []
  let latest = null
  for (const c of list) {
    if (!c) continue
    const t = c.created_at || c.modified_at
    if (!t) continue
    const d = typeof t === 'number' ? new Date(t) : parseDate(t)
    if (d && (!latest || d > latest)) latest = d
  }
  return latest
}

// Real checks only (ignore blank seed entries) — matches the delete guard.
export function realChecks(asset) {
  const list = Array.isArray(asset?.check) ? asset.check : []
  return list.filter((c) => c && (c.remark || c.created_at || (Array.isArray(c.file) && c.file.length)))
}

// Is the asset due for a periodic check? Due when never checked, or the last
// check is older than `months`. Retired assets are never "due".
export function checkDueState(asset, months = 6) {
  if (asset?.status === 'retired') return { due: false, last: null, never: false }
  const checks = realChecks(asset)
  const last = lastCheckAt(asset)
  if (!checks.length || !last) return { due: true, last: null, never: true }
  const cutoff = new Date()
  cutoff.setMonth(cutoff.getMonth() - months)
  return { due: last < cutoff, last, never: false }
}

/* ── maintenance ─────────────────────────────────────────────────────────── */
export const MAINT_STATUS = [
  { value: 'requested',   label: 'แจ้งซ่อม' },
  { value: 'in_progress', label: 'กำลังซ่อม' },
  { value: 'done',        label: 'เสร็จแล้ว' },
  { value: 'cancelled',   label: 'ยกเลิก' },
]
const MAINT_TONES = {
  requested:   'bg-amber-50 text-amber-700 ring-amber-200',
  in_progress: 'bg-sky-50 text-sky-700 ring-sky-200',
  done:        'bg-emerald-50 text-emerald-700 ring-emerald-200',
  cancelled:   'bg-slate-100 text-slate-500 ring-slate-200',
}
export function maintStatusInfo(status) {
  const v = String(status || 'requested').trim() || 'requested'
  const found = MAINT_STATUS.find((s) => s.value === v)
  return { value: v, label: found ? found.label : v, tone: MAINT_TONES[v] || 'bg-slate-100 text-slate-600 ring-slate-200' }
}
// Aggregate a maintenance list: count · total cost (excludes cancelled) · open count.
export function maintenanceTotals(records) {
  const rows = Array.isArray(records) ? records : []
  let totalCost = 0, open = 0
  for (const r of rows) {
    if (r?.status !== 'cancelled') totalCost += Number(r?.cost || 0)
    if (r?.status === 'requested' || r?.status === 'in_progress') open += 1
  }
  return { count: rows.length, totalCost, open }
}

/* ── images ──────────────────────────────────────────────────────────────────
 * asset.image[] — a gallery; one entry may carry is_profile=true. */
export function assetImages(asset) {
  return (Array.isArray(asset?.image) ? asset.image : []).filter((f) => f && f.location)
}
export function profileImage(asset) {
  const imgs = assetImages(asset)
  return imgs.find((f) => f.is_profile) || imgs[0] || null
}
// A displayable src for an image file object: the proxied full image (thumbnails
// are inline data-URIs, used directly where available).
export function imageProxySrc(fileObj) {
  if (!fileObj?.location) return fileObj?.thumbnail || ''
  return `/api/asset-file/${fileObj.location.split('/').map(encodeURIComponent).join('/')}`
}
export function imageThumbSrc(fileObj) {
  return fileObj?.thumbnail || imageProxySrc(fileObj)
}

/* ── warranty ────────────────────────────────────────────────────────────────
 * → { state: 'none'|'active'|'soon'|'expired', date, daysLeft } */
export function warrantyState(asset, soonDays = 60) {
  const d = parseDate(asset?.warranty_expire)
  if (!d) return { state: 'none', date: null, daysLeft: null }
  const now = new Date()
  const daysLeft = Math.round((d - now) / 86400000)
  if (daysLeft < 0) return { state: 'expired', date: d, daysLeft }
  if (daysLeft <= soonDays) return { state: 'soon', date: d, daysLeft }
  return { state: 'active', date: d, daysLeft }
}

/* ── accounting class ────────────────────────────────────────────────────────
 * Two kinds of asset, and the difference is the accountant's, not the clinic's:
 *
 *   depreciate = true   ทรัพย์สินถาวร — capitalised; ค่าเสื่อม is spread over
 *                       useful_life, so useful_life and the book-value estimate
 *                       both apply.
 *   depreciate = false  a one-time purchase the accountant books as an ordinary
 *                       company expense in the period it was bought. There is no
 *                       ค่าเสื่อม, so useful_life and book value are meaningless
 *                       and must not be shown — a residual value on something
 *                       already fully expensed is exactly the kind of number that
 *                       ends up double-counted.
 *
 * Rows created before the column existed read as `undefined`, which must mean
 * TRUE: depreciating was the only behaviour the register had. Hence `!== false`
 * rather than a truthiness test. */
export function isDepreciable(asset) {
  return asset?.depreciate !== false
}

export const ACCT_CLASS = [
  { value: true,  label: 'ทรัพย์สินถาวร', hint: 'คิดค่าเสื่อมตามอายุการใช้งาน', short: 'คิดค่าเสื่อม',
    tone: 'bg-indigo-50 text-indigo-700 ring-indigo-200' },
  { value: false, label: 'ค่าใช้จ่าย', hint: 'ซื้อครั้งเดียว ลงเป็นค่าใช้จ่ายทันที', short: 'ไม่คิดค่าเสื่อม',
    tone: 'bg-slate-100 text-slate-600 ring-slate-200' },
]

export function acctClassInfo(asset) {
  return ACCT_CLASS.find((c) => c.value === isDepreciable(asset)) || ACCT_CLASS[0]
}

/* ── depreciation (ESTIMATE only) ─────────────────────────────────────────────
 * Straight-line book-value estimate for a rough on-screen figure. The ACTUAL
 * depreciation is applied by the outsourced accounting team — this is a signal,
 * not an authoritative number. 0 salvage.
 *
 * Useful life (อายุการใช้งาน) is per-asset: `asset.useful_life` in years, set in
 * the editor. Rows created before the column existed store 0 — those (and any
 * explicit 0) fall back to DEFAULT_USEFUL_LIFE so the estimate never divides by
 * zero. Pass `years` to override for a what-if. */
export const DEFAULT_USEFUL_LIFE = 5

// The effective useful life for an asset, in years. Always ≥ 1.
export function usefulLife(asset) {
  const n = Number(asset?.useful_life)
  return Number.isFinite(n) && n > 0 ? n : DEFAULT_USEFUL_LIFE
}

export function depreciationEstimate(asset, years) {
  // An expensed asset has no book value to estimate — see isDepreciable().
  if (!isDepreciable(asset)) return null
  const price = Number(asset?.price || 0)
  const age = assetAge(asset?.date)
  if (!price || !age) return null
  const life = Number(years) > 0 ? Number(years) : usefulLife(asset)
  const lifeMonths = life * 12
  const used = Math.min(age.months, lifeMonths)
  const bookValue = Math.max(0, price * (1 - used / lifeMonths))
  const perYear = price / life
  return { price, years: life, bookValue, perYear, fullyDepreciated: age.months >= lifeMonths }
}
